import React, { useState, useEffect } from 'react';
import api from '../api/axios';
import { DocumentPhrasesEditor } from '../components/DocumentPhrases';

interface CompanySettingsData {
  id: number;
  registered_name?: string;
  resolved_names?: Record<string, string>; resolved_signatories?: Record<string, string>;
  legal_name_uz?: string; legal_name_ru?: string; legal_name_en?: string;
  tagline_uz?: string; tagline_ru?: string; tagline_en?: string;
  address_uz?: string; address_ru?: string; address_en?: string;
  letterhead_address_uz?: string; letterhead_address_en?: string;
  phone?: string; email?: string; website?: string; telegram?: string; linkedin?: string;
  bank_details?: string; logo_path?: string; hero_image_path?: string; inn?: string;
  bank_account_uzs?: string; bank_name_uzs?: string; bank_code_uzs?: string; bank_swift_uzs?: string;
  bank_account_usd?: string; bank_name_usd?: string; bank_code_usd?: string; bank_swift_usd?: string;
  bank_account_eur?: string; bank_name_eur?: string; bank_code_eur?: string; bank_swift_eur?: string;
  contract_party_name_uz?: string; contract_party_name_ru?: string; contract_party_name_en?: string;
  signatory_name_uz?: string; signatory_name_ru?: string; signatory_name_en?: string;
  signatory_name_genitive_ru?: string;
  signatory_position_uz?: string; signatory_position_ru?: string; signatory_position_en?: string;
  signatory_basis_uz?: string; signatory_basis_ru?: string; signatory_basis_en?: string;
  legal_form?: string; is_vat_payer?: boolean | null; vat_percent?: number | null;
}

// Shartnoma tarafi (muqaddima/imzo bloki) - uch tilli maydonlar. Qarang
// backend/app/core/contract_text.py resolve_seller_identity().
const LANGS: { key: 'uz' | 'ru' | 'en'; label: string }[] = [
  { key: 'uz', label: "🇺🇿 O'zbekcha" },
  { key: 'ru', label: '🇷🇺 Ruscha' },
  { key: 'en', label: '🇬🇧 Inglizcha' },
];
const SIGNATORY_FIELDS: { prefix: string; label: string; hint: string; placeholders: Record<string, string> }[] = [
  { prefix: 'contract_party_name', label: 'Shartnomadagi yuridik nomi',
    hint: "Muqaddimada shu ko'rinishda yoziladi. Bo'sh bo'lsa - yuqoridagi yuridik nom. To'liq yuridik nom to'ldirilgan bo'lsa - ishlatilmaydi.",
    placeholders: { uz: '"KOMPANIYA" MChJ', ru: 'ООО "КОМПАНИЯ"', en: 'LLC "COMPANY"' } },
  { prefix: 'signatory_name', label: 'Imzolovchi F.I.Sh.',
    hint: "Shartnoma imzo bloki VA dalolatnomadagi Pudratchi imzolovchisi - har bir tilda shu qiymat. YaTT'da bo'sh bo'lsa - to'liq yuridik nomdan (Jo'rayev Sh.Sh.).",
    placeholders: { uz: 'Familiya I.O.', ru: 'Фамилия И.О.', en: 'Surname I.O.' } },
  { prefix: 'signatory_position', label: 'Imzolovchi lavozimi ("в лице кого")',
    hint: "Ruschada qaratqich kelishigida (Директора). YaTT'da bo'sh qoldiring - «в лице ...» qismi chiqmaydi.",
    placeholders: { uz: 'Direktori', ru: 'Директора', en: 'Director' } },
  { prefix: 'signatory_basis', label: 'Harakat asosi ("на основании ...")',
    hint: 'Erkin matn - guvohnoma raqami va sanasini ham yozish mumkin.',
    placeholders: { uz: "Ustav / davlat ro'yxatidan o'tganlik guvohnomasi № ... sana ...", ru: 'Устава / свидетельства о государственной регистрации № ... от ...', en: 'the Charter / state registration certificate No. ... dated ...' } },
];

// Har bir valyutadagi bank hisobi shartnomalarda AYNAN shu valyutaga mos
// hisob ko'rsatilishi uchun ALOHIDA saqlanadi (moliyaviy xato tuzatishi -
// ilgari bitta umumiy hisob har doim ko'rsatilardi, shartnoma valyutasidan
// qat'i nazar). Qarang backend/app/core/i18n.py get_company_bank_account().
const CURRENCY_BLOCKS: { key: 'uzs' | 'usd' | 'eur'; label: string }[] = [
  { key: 'uzs', label: "🇺🇿 UZS (so'm)" },
  { key: 'usd', label: '🇺🇸 USD (dollar)' },
  { key: 'eur', label: '🇪🇺 EUR (yevro)' },
];

const emptyForm = {
  registered_name: '',
  legal_name_uz: '', legal_name_ru: '', legal_name_en: '',
  tagline_uz: '', tagline_ru: '', tagline_en: '',
  address_uz: '', address_ru: '', address_en: '',
  letterhead_address_uz: '', letterhead_address_en: '',
  phone: '', email: '', website: '', telegram: '', linkedin: '',
  bank_details: '', logo_path: '', hero_image_path: '', inn: '',
  bank_account_uzs: '', bank_name_uzs: '', bank_code_uzs: '', bank_swift_uzs: '',
  bank_account_usd: '', bank_name_usd: '', bank_code_usd: '', bank_swift_usd: '',
  bank_account_eur: '', bank_name_eur: '', bank_code_eur: '', bank_swift_eur: '',
  contract_party_name_uz: '', contract_party_name_ru: '', contract_party_name_en: '',
  signatory_name_uz: '', signatory_name_ru: '', signatory_name_en: '',
  signatory_name_genitive_ru: '',
  signatory_position_uz: '', signatory_position_ru: '', signatory_position_en: '',
  signatory_basis_uz: '', signatory_basis_ru: '', signatory_basis_en: '',
  legal_form: 'llc', is_vat_payer: true as boolean, vat_percent: 12 as number | null,
};

const CompanySettings: React.FC = () => {
  const [form, setForm] = useState(emptyForm);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState('');
  // Hujjatlarda amalda chiqadigan nom/imzolovchi (backend qoidasi natijasi, saqlangandan keyin yangilanadi)
  const [resolved, setResolved] = useState<{ names: Record<string, string>; signatories: Record<string, string> }>({ names: {}, signatories: {} });
  const fromRegistered = form.registered_name.trim() !== '';

  const fetchData = async () => {
    setLoading(true);
    try {
      const res = await api.get<CompanySettingsData>('/company-settings');
      const d = res.data;
      setResolved({ names: d.resolved_names || {}, signatories: d.resolved_signatories || {} });
      setForm({
        registered_name: d.registered_name || '',
        legal_name_uz: d.legal_name_uz || '', legal_name_ru: d.legal_name_ru || '', legal_name_en: d.legal_name_en || '',
        tagline_uz: d.tagline_uz || '', tagline_ru: d.tagline_ru || '', tagline_en: d.tagline_en || '',
        address_uz: d.address_uz || '', address_ru: d.address_ru || '', address_en: d.address_en || '',
        letterhead_address_uz: d.letterhead_address_uz || '', letterhead_address_en: d.letterhead_address_en || '',
        phone: d.phone || '', email: d.email || '', website: d.website || '', telegram: d.telegram || '', linkedin: d.linkedin || '',
        bank_details: d.bank_details || '', logo_path: d.logo_path || '', hero_image_path: d.hero_image_path || '', inn: d.inn || '',
        bank_account_uzs: d.bank_account_uzs || '', bank_name_uzs: d.bank_name_uzs || '', bank_code_uzs: d.bank_code_uzs || '', bank_swift_uzs: d.bank_swift_uzs || '',
        bank_account_usd: d.bank_account_usd || '', bank_name_usd: d.bank_name_usd || '', bank_code_usd: d.bank_code_usd || '', bank_swift_usd: d.bank_swift_usd || '',
        bank_account_eur: d.bank_account_eur || '', bank_name_eur: d.bank_name_eur || '', bank_code_eur: d.bank_code_eur || '', bank_swift_eur: d.bank_swift_eur || '',
        contract_party_name_uz: d.contract_party_name_uz || '', contract_party_name_ru: d.contract_party_name_ru || '', contract_party_name_en: d.contract_party_name_en || '',
        signatory_name_uz: d.signatory_name_uz || '', signatory_name_ru: d.signatory_name_ru || '', signatory_name_en: d.signatory_name_en || '',
        signatory_name_genitive_ru: d.signatory_name_genitive_ru || '',
        signatory_position_uz: d.signatory_position_uz || '', signatory_position_ru: d.signatory_position_ru || '', signatory_position_en: d.signatory_position_en || '',
        signatory_basis_uz: d.signatory_basis_uz || '', signatory_basis_ru: d.signatory_basis_ru || '', signatory_basis_en: d.signatory_basis_en || '',
        // NULL = QQS to'lovchi (backend bilan bir xil standart)
        legal_form: d.legal_form || 'llc', is_vat_payer: d.is_vat_payer !== false,
        vat_percent: d.vat_percent ?? 12,  // NULL = 12 (backend standarti)
      });
    } catch (e) {
      setError("Ma'lumotlarni yuklashda xato");
    } finally { setLoading(false); }
  };

  useEffect(() => { fetchData(); }, []);

  const handleSubmit = async () => {
    setSaving(true); setError(''); setSaved(false);
    try {
      const r = await api.put<CompanySettingsData>('/company-settings', form);
      setResolved({ names: r.data.resolved_names || {}, signatories: r.data.resolved_signatories || {} });
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (e: any) {
      setError(e.response?.data?.detail || 'Xatolik yuz berdi');
    } finally { setSaving(false); }
  };

  if (loading) return <div className="p-6 text-gray-400">Yuklanmoqda...</div>;

  return (
    <div className="p-3 md:p-6 max-w-3xl">
      <h2 className="text-xl md:text-2xl font-bold text-gray-800 mb-1">⚙️ Kompaniya ma'lumotlari</h2>
      <p className="text-xs md:text-sm text-gray-500 mb-4 md:mb-6">
        Bu yerdagi ma'lumotlar tijorat takliflari va xarid so'rovlari PDF hujjatlarining
        header/footer qismida ko'rsatiladi. Faqat admin tahrirlashi mumkin.
      </p>

      {error && <div className="bg-red-50 text-red-600 p-3 rounded-lg mb-4 text-sm">{error}</div>}
      {saved && <div className="bg-green-50 text-green-700 p-3 rounded-lg mb-4 text-sm">✅ Saqlandi</div>}

      <div className="bg-white rounded-xl shadow p-4 md:p-6 space-y-5">
        {/* To'liq yuridik nom - yagona manba */}
        <div>
          <p className="text-xs font-semibold text-gray-500 uppercase mb-2 border-b pb-1">🪪 To&apos;liq yuridik nom (yagona manba)</p>
          <input value={form.registered_name} onChange={e => setForm({ ...form, registered_name: e.target.value })}
            placeholder="Masalan: JO'RAYEV SHAHRIDDIN SHAVKAT O'G'LI"
            className="w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 text-xs md:text-sm" />
          <p className="text-xs text-gray-400 mt-1">
            Ro&apos;yxatdan o&apos;tgan nom, tilga bog&apos;liq emas. To&apos;ldirilsa barcha hujjatlarda (sarlavha, footer, shartnoma,
            rekvizitlar, dalolatnoma) nom shundan quriladi (YaTT: YaTT «…» / ИП «…» / SP …; MChJ: «…» MChJ / ООО «…» / … LLC);
            pastdagi uch tilli yuridik nom va «Shartnomadagi yuridik nomi» e&apos;tiborga olinmaydi. YaTT&apos;da imzolovchi F.I.Sh.
            bo&apos;sh bo&apos;lsa - shu nomdan (masalan «Jo&apos;rayev Sh.Sh.»).
          </p>
          {(resolved.names.uz || resolved.signatories.uz) && (
            <div className="mt-2 text-xs bg-gray-50 border rounded-lg p-2 grid grid-cols-1 md:grid-cols-3 gap-2">
              {LANGS.map(l => (
                <div key={l.key}>
                  <span className="text-gray-500">{l.label}:</span>{' '}
                  <span className="font-medium">{resolved.names[l.key] || '—'}</span>
                  <div className="text-gray-500">Imzolovchi: {resolved.signatories[l.key] || '—'}</div>
                </div>
              ))}
              <p className="md:col-span-3 text-gray-400">Hujjatlarda hozir shunday chiqadi (saqlangan qiymatlar bo&apos;yicha).</p>
            </div>
          )}
        </div>

        {/* Yuridik nomi - 3 tilda */}
        <div className={fromRegistered ? 'opacity-50' : ''}>
          <p className="text-xs font-semibold text-gray-500 uppercase mb-2 border-b pb-1">🌐 Yuridik nomi (uch tilda){fromRegistered ? " - to'liq yuridik nomdan quriladi" : ''}</p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">🇺🇿 O'zbekcha</label>
              <input disabled={fromRegistered} value={form.legal_name_uz} onChange={e => setForm({ ...form, legal_name_uz: e.target.value })}
                placeholder="Masalan: Lab Instruments MChJ"
                className="w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 text-xs md:text-sm" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">🇷🇺 Ruscha</label>
              <input disabled={fromRegistered} value={form.legal_name_ru} onChange={e => setForm({ ...form, legal_name_ru: e.target.value })}
                placeholder="Masalan: ООО Lab Instruments"
                className="w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 text-xs md:text-sm" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">🇬🇧 Inglizcha</label>
              <input disabled={fromRegistered} value={form.legal_name_en} onChange={e => setForm({ ...form, legal_name_en: e.target.value })}
                placeholder="Masalan: Lab Instruments LLC"
                className="w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 text-xs md:text-sm" />
            </div>
          </div>
        </div>

        {/* Sarlavha ostidagi qisqa tavsif - 3 tilda */}
        <div>
          <p className="text-xs font-semibold text-gray-500 uppercase mb-2 border-b pb-1">🏷️ Qisqa tavsif / tagline (uch tilda)</p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">🇺🇿 O'zbekcha</label>
              <input value={form.tagline_uz} onChange={e => setForm({ ...form, tagline_uz: e.target.value })}
                placeholder="Masalan: Analitik asboblar • Rasxod materiallari • Xizmat • Trening"
                className="w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 text-xs md:text-sm" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">🇷🇺 Ruscha</label>
              <input value={form.tagline_ru} onChange={e => setForm({ ...form, tagline_ru: e.target.value })}
                placeholder="Masalan: Аналитические приборы • Расходные материалы • Сервис • Обучение"
                className="w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 text-xs md:text-sm" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">🇬🇧 Inglizcha</label>
              <input value={form.tagline_en} onChange={e => setForm({ ...form, tagline_en: e.target.value })}
                placeholder="Masalan: Analytical Instruments • Consumables • Service • Training"
                className="w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 text-xs md:text-sm" />
            </div>
          </div>
        </div>

        {/* Manzil - 3 tilda */}
        <div>
          <p className="text-xs font-semibold text-gray-500 uppercase mb-2 border-b pb-1">📍 Manzil (uch tilda)</p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">🇺🇿 O'zbekcha</label>
              <textarea value={form.address_uz} onChange={e => setForm({ ...form, address_uz: e.target.value })}
                rows={2} className="w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 text-xs md:text-sm" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">🇷🇺 Ruscha</label>
              <textarea value={form.address_ru} onChange={e => setForm({ ...form, address_ru: e.target.value })}
                rows={2} className="w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 text-xs md:text-sm" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">🇬🇧 Inglizcha</label>
              <textarea value={form.address_en} onChange={e => setForm({ ...form, address_en: e.target.value })}
                rows={2} className="w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 text-xs md:text-sm" />
            </div>
          </div>
        </div>

        {/* Hujjat footer'idagi manzil - ixtiyoriy, qatorlarga bo'lingan */}
        <div>
          <p className="text-xs font-semibold text-gray-500 uppercase mb-1 border-b pb-1">📄 Hujjat footer'idagi manzil (ixtiyoriy)</p>
          <p className="text-xs text-gray-500 mb-2">
            PDF va Word hujjatlarining pastki qismida (uz | en) chiqadi, har qator alohida. Bo'sh bo'lsa - yuqoridagi manzil bitta qatorda.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">🇺🇿 O'zbekcha</label>
              <textarea value={form.letterhead_address_uz} onChange={e => setForm({ ...form, letterhead_address_uz: e.target.value })}
                rows={2} className="w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 text-xs md:text-sm" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">🇬🇧 Inglizcha</label>
              <textarea value={form.letterhead_address_en} onChange={e => setForm({ ...form, letterhead_address_en: e.target.value })}
                rows={2} className="w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 text-xs md:text-sm" />
            </div>
          </div>
        </div>

        {/* Kontaktlar */}
        <div>
          <p className="text-xs font-semibold text-gray-500 uppercase mb-2 border-b pb-1">📞 Kontaktlar</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="block text-xs md:text-sm font-medium text-gray-700 mb-1">Telefon</label>
              <input value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })}
                placeholder="+998509996600"
                className="w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 text-xs md:text-sm" />
            </div>
            <div>
              <label className="block text-xs md:text-sm font-medium text-gray-700 mb-1">Email</label>
              <input value={form.email} onChange={e => setForm({ ...form, email: e.target.value })}
                placeholder="sales@labin.uz"
                className="w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 text-xs md:text-sm" />
            </div>
            <div>
              <label className="block text-xs md:text-sm font-medium text-gray-700 mb-1">Sayt</label>
              <input value={form.website} onChange={e => setForm({ ...form, website: e.target.value })}
                placeholder="www.labin.uz"
                className="w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 text-xs md:text-sm" />
            </div>
            <div>
              <label className="block text-xs md:text-sm font-medium text-gray-700 mb-1">Telegram</label>
              <input value={form.telegram} onChange={e => setForm({ ...form, telegram: e.target.value })}
                placeholder="@labin_uz"
                className="w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 text-xs md:text-sm" />
            </div>
            <div>
              <label className="block text-xs md:text-sm font-medium text-gray-700 mb-1">LinkedIn</label>
              <input value={form.linkedin} onChange={e => setForm({ ...form, linkedin: e.target.value })}
                placeholder="Lab Instruments LLC"
                className="w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 text-xs md:text-sm" />
            </div>
          </div>
        </div>

        {/* INN */}
        <div>
          <p className="text-xs font-semibold text-gray-500 uppercase mb-2 border-b pb-1">🪪 STIR (INN)</p>
          <input value={form.inn} onChange={e => setForm({ ...form, inn: e.target.value })}
            placeholder="311 687 326"
            className="w-full md:w-1/2 border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 text-xs md:text-sm" />
        </div>

        {/* Valyuta bo'yicha bank hisoblari */}
        <div>
          <p className="text-xs font-semibold text-gray-500 uppercase mb-2 border-b pb-1">🏦 Bank hisoblari (valyuta bo'yicha)</p>
          <p className="text-xs text-gray-500 mb-3">
            Shartnomalarda har doim shartnoma valyutasiga MOS hisob ko'rsatiladi. Valyuta uchun hisob
            kiritilmagan bo'lsa - shartnomada bank qatori ko'rsatilmaydi (noto'g'ri hisobni ko'rsatishdan
            ko'ra xavfsizroq).
          </p>
          <div className="space-y-4">
            {CURRENCY_BLOCKS.map(({ key, label }) => (
              <div key={key} className="border rounded-lg p-3 bg-gray-50">
                <p className="text-xs font-semibold text-gray-600 mb-2">{label}</p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Hisob raqami</label>
                    <input value={(form as any)[`bank_account_${key}`]}
                      onChange={e => setForm({ ...form, [`bank_account_${key}`]: e.target.value })}
                      className="w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 text-xs md:text-sm" />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Bank nomi</label>
                    <input value={(form as any)[`bank_name_${key}`]}
                      onChange={e => setForm({ ...form, [`bank_name_${key}`]: e.target.value })}
                      placeholder="Kapitalbank AT"
                      className="w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 text-xs md:text-sm" />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">MFO</label>
                    <input value={(form as any)[`bank_code_${key}`]}
                      onChange={e => setForm({ ...form, [`bank_code_${key}`]: e.target.value })}
                      placeholder="01158"
                      className="w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 text-xs md:text-sm" />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">SWIFT (xalqaro, ixtiyoriy)</label>
                    <input value={(form as any)[`bank_swift_${key}`]}
                      onChange={e => setForm({ ...form, [`bank_swift_${key}`]: e.target.value })}
                      placeholder="KACHUZ22"
                      className="w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 text-xs md:text-sm" />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Shartnoma tarafi: yuridik shakl, QQS, imzolovchi */}
        <div>
          <p className="text-xs font-semibold text-gray-500 uppercase mb-2 border-b pb-1">✍️ Shartnomalar: yuridik shakl va imzolovchi</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
            <div>
              <label className="block text-xs md:text-sm font-medium text-gray-700 mb-1">Yuridik shakl</label>
              <select value={form.legal_form} onChange={e => setForm({ ...form, legal_form: e.target.value })}
                className="w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 text-xs md:text-sm">
                <option value="llc">MChJ / ООО / LLC (yuridik shaxs)</option>
                <option value="sole_proprietor">YaTT / ИП (yakka tartibdagi tadbirkor)</option>
              </select>
            </div>
            <div className="flex items-end">
              <label className="flex items-center gap-2 text-xs md:text-sm text-gray-700 pb-2">
                <input type="checkbox" checked={form.is_vat_payer}
                  onChange={e => setForm({ ...form, is_vat_payer: e.target.checked })} />
                QQS to'lovchi (belgilanmasa - shartnomalarda "QQSsiz / без НДС")
              </label>
            </div>
            {form.is_vat_payer && (
              <div>
                <label className="block text-xs md:text-sm font-medium text-gray-700 mb-1">QQS stavkasi, % (hisob-fakturalar)</label>
                <input type="number" min={0} max={100} step="0.01" value={form.vat_percent ?? ''}
                  onChange={e => setForm({ ...form, vat_percent: e.target.value === '' ? null : Number(e.target.value) })}
                  className="w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 text-xs md:text-sm" />
                <p className="text-xs text-gray-400 mt-1">Narx QQS ichida - QQS ajratib ko&apos;rsatiladi, ustiga qo&apos;shilmaydi. Bo&apos;sh = 12%.</p>
              </div>
            )}
          </div>
          <div className="space-y-3">
            {SIGNATORY_FIELDS.map(f => (
              <div key={f.prefix}>
                <p className="text-xs font-medium text-gray-700 mb-1">{f.label}</p>
                {f.hint && <p className="text-xs text-gray-400 mb-1">{f.hint}</p>}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  {LANGS.map(l => (
                    <div key={l.key}>
                      <label className="block text-xs text-gray-500 mb-1">{l.label}</label>
                      <input value={(form as any)[`${f.prefix}_${l.key}`]}
                        disabled={fromRegistered && f.prefix === 'contract_party_name'}
                        onChange={e => setForm({ ...form, [`${f.prefix}_${l.key}`]: e.target.value })}
                        placeholder={f.placeholders[l.key]}
                        className="w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 text-xs md:text-sm" />
                    </div>
                  ))}
                </div>
              </div>
            ))}
            <div>
              <p className="text-xs font-medium text-gray-700 mb-1">Imzolovchi F.I.Sh. - ruscha, «в лице кого» (qaratqich kelishigi)</p>
              <p className="text-xs text-gray-400 mb-1">Faqat «в лице Директора ...» uchun. Imzo ostida yuqoridagi ruscha F.I.Sh. qoladi. Bo'sh bo'lsa - yuqoridagi ruscha F.I.Sh. o'zgarishsiz yoziladi.</p>
              <input value={form.signatory_name_genitive_ru}
                onChange={e => setForm({ ...form, signatory_name_genitive_ru: e.target.value })}
                placeholder="Фамилии И.О. (Жураева Ш.Ш.)"
                className="w-full md:w-1/3 border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 text-xs md:text-sm" />
            </div>
          </div>
        </div>

        {/* Eski umumiy bank matni va logotip */}
        <div>
          <p className="text-xs font-semibold text-gray-500 uppercase mb-2 border-b pb-1">📝 Qo'shimcha izoh va logotip</p>
          <div className="grid grid-cols-1 gap-3">
            <div>
              <label className="block text-xs md:text-sm font-medium text-gray-700 mb-1">Bank rekvizitlari (erkin izoh)</label>
              <textarea value={form.bank_details} onChange={e => setForm({ ...form, bank_details: e.target.value })}
                rows={3} placeholder="Qo'shimcha eslatmalar (yuqoridagi valyuta bo'yicha hisoblar shartnomalarda ishlatiladi)"
                className="w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 text-xs md:text-sm" />
            </div>
            <div>
              <label className="block text-xs md:text-sm font-medium text-gray-700 mb-1">Logotip fayli yo'li</label>
              <input value={form.logo_path} onChange={e => setForm({ ...form, logo_path: e.target.value })}
                placeholder="static/logo.png"
                className="w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 text-xs md:text-sm" />
              <p className="text-xs text-gray-400 mt-1">
                Serverdagi static/ papkasidagi fayl. Bo&apos;sh bo&apos;lsa hujjatlar logotipsiz chiqadi (asosiy kompaniyada - standart logotip).
                Fayl yuklash imkoniyati keyinroq qo&apos;shiladi.
              </p>
            </div>
            <div>
              <label className="block text-xs md:text-sm font-medium text-gray-700 mb-1">Taklif sarlavhasidagi rasm (ixtiyoriy)</label>
              <input value={form.hero_image_path} onChange={e => setForm({ ...form, hero_image_path: e.target.value })}
                placeholder="static/hero-kompaniya.jpg"
                className="w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 text-xs md:text-sm" />
              <p className="text-xs text-gray-400 mt-1">
                Tijorat taklifi va xarid so&apos;rovi PDF sarlavhasining o&apos;ng tomonidagi rasm. Bo&apos;sh bo&apos;lsa rasm chiqmaydi.
                Hujjat QR kodi faqat yuqoridagi &quot;Sayt&quot; maydonidan olinadi; sayt bo&apos;sh bo&apos;lsa QR chiqmaydi.
              </p>
            </div>
          </div>
        </div>

        <div className="flex justify-end pt-2">
          <button onClick={handleSubmit} disabled={saving}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 text-sm">
            {saving ? 'Saqlanmoqda...' : 'Saqlash'}
          </button>
        </div>
      </div>

      {/* Uch tilli hujjat ma'lumotnomasi - o'z saqlash tugmalari bilan (yuqoridagi "Saqlash"ga bog'liq emas) */}
      <div className="bg-white rounded-xl shadow p-4 md:p-6 mt-4">
        <p className="text-xs font-semibold text-gray-500 uppercase mb-2 border-b pb-1">📚 Hujjat ma&apos;lumotnomasi (uch tilda)</p>
        <DocumentPhrasesEditor />
      </div>
    </div>
  );
};

export default CompanySettings;
